/**
 * The placeholder mark for an organization with no uploaded logo.
 *
 * This is the ordinary path, not a fallback for failure: most directory records will never have a
 * logo, so the monogram has to look deliberate rather than broken.
 */

/**
 * Words shared by nearly every record in a directory of organizations. They carry no
 * distinguishing information, so they are skipped when choosing initials — "Masjid Al-Noor" reads
 * as "AN" rather than "MA", "Congregation Beth Shalom" as "BS" rather than "CB" — and two
 * organizations in the same city stay distinguishable at 64 pixels.
 *
 * The list has to span traditions or it silently favours one. Until 2026-08-29 it held only
 * "masjid", "mosque" and "islamic", so an Islamic centre got a monogram chosen from its
 * distinctive words while a synagogue, church, temple or gurdwara got one chosen from the word
 * every one of its neighbours also uses. That is the same defect as hard-coding a faith into
 * copy: it just showed up as a worse-looking mark instead of a wrong sentence.
 *
 * Deliberately *not* here: "saint"/"st" and "beth"/"bet". They lead names whose remainder is
 * often a single word, and "St Mary" reading "SM" beats it reading "M".
 */
const GENERIC_WORDS = new Set([
  // Articles and corporate filler.
  "the",
  "of",
  "and",
  "inc",
  "incorporated",
  "corp",
  // What the organization calls its building or its body, across traditions.
  "masjid",
  "mosque",
  "islamic",
  "church",
  "chapel",
  "cathedral",
  "parish",
  "congregation",
  "synagogue",
  "temple",
  "tabernacle",
  "ministries",
  "ministry",
  "fellowship",
  "assembly",
  "mandir",
  "gurdwara",
  "vihara",
  "sangha",
  "worship",
  "house",
  // Generic nonprofit words, faith or no faith.
  "center",
  "centre",
  "society",
  "association",
  "foundation",
  "community",
  "institute",
  "trust",
]);

export function monogram(displayName: string): string {
  const words = displayName
    .split(/[\s-]+/)
    // Strip punctuation but keep letters and digits from any script: this runs on Arabic and
    // transliterated names as often as on English ones.
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((word) => word.length > 0);

  const distinctive = words.filter((word) => !GENERIC_WORDS.has(word.toLowerCase()));
  // If every word is generic, initials from the generic words beat showing nothing at all.
  const chosen = distinctive.length > 0 ? distinctive : words;

  return chosen
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * How many monogram tints the palette holds. See `--mark-0`…`--mark-5` in `styles.css`.
 *
 * Six, not a computed hue: a hash into HSL puts the ink at whatever contrast it lands on, while
 * six measured pairs cannot generate an unreadable mark.
 */
export const MARK_TINTS = 6;

/**
 * Which of the six tints an organization's monogram is drawn in.
 *
 * Keyed on the **display name** rather than on the id, for two reasons that point the same way:
 * the name is what a donor recognises, and it is the one thing every caller of `OrgMark` already
 * holds — `ArrivalBand` on iOS has a name and no id at all. Threading an id to it would mean the
 * band drew a different colour from the row a donor tapped to get there.
 *
 * FNV-1a over the name's **UTF-8 bytes**, which is the whole reason this is spelled out rather
 * than left to any convenient hash: `OrgMark.swift` computes the identical number, so the same
 * organization is the same colour on the phone and on the web. Iterating UTF-16 code units here
 * and unicode scalars there would agree on every Latin name and diverge on the first Arabic one.
 */
export function markTint(displayName: string): number {
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(displayName)) {
    hash ^= byte;
    // `Math.imul` rather than `*`: the product overflows a double's exact-integer range and would
    // start rounding, which is a hash that quietly stops matching Swift's.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  /*
    Multiply-shift, not `hash % MARK_TINTS`.

    The modulo takes the hash's low bits, which is where FNV-1a is weakest, and it showed on the
    first rendered screen: eleven organizations landed on five tints, one of them five times, and
    a sixth tint went unused. That is the feature failing quietly — the tint exists so a directory
    does not draw one tile over and over.

    This maps the whole 32-bit range evenly onto the six instead. `hash * MARK_TINTS` reaches 2^35,
    which a double still holds exactly, so it stays an integer operation.
  */
  return Math.floor((hash * MARK_TINTS) / 2 ** 32);
}
